// ─── AdminPricing.jsx ───
// New file: src/pages/admin/AdminPricing.jsx
// Add to App.js: import { AdminPricing } from "@/pages/admin/AdminPricing";
// Add route: <Route path="/admin/pricing" element={<AdminPricing />} />
// Add to sidebar in AdminPages.jsx links array:
//   { to: "/admin/pricing", icon: CalendarDays, label: "Pricing" }
// Add CalendarDays to lucide imports in AdminPages.jsx

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Car, BookOpen, MessageSquare, LayoutDashboard, LogOut, Menu, X, Users, CalendarDays, Trash2, Pencil, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_eurocar-start/artifacts/io6ga27q_eurocars-logo2.png";

// ─── Reuse AdminLayout (copied here to keep file self-contained) ───
function AdminLayout({ children }) {
  const { admin, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [admin, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!admin) return null;

  const links = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/cars", icon: Car, label: "Cars" },
    { to: "/admin/bookings", icon: BookOpen, label: "Bookings" },
    { to: "/admin/messages", icon: MessageSquare, label: "Messages" },
    { to: "/admin/users", icon: Users, label: "Users" },
    { to: "/admin/pricing", icon: CalendarDays, label: "Pricing" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden lg:flex w-64 bg-slate-900 flex-col fixed inset-y-0 left-0 z-30">
        <div className="p-6 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Euro Cars" className="h-10 w-auto object-contain" />
          </Link>
          <p className="font-dm-sans text-xs text-slate-400 mt-2">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {links.map((link) => (
            <Link key={link.to} to={link.to} className={`flex items-center gap-3 px-4 py-3 rounded-xl font-outfit text-sm font-medium transition-all ${location.pathname === link.to ? "bg-orange-500 text-white shadow-lg shadow-orange-500/20" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}>
              <link.icon className="w-5 h-5" />{link.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800">
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl font-outfit text-sm font-medium text-red-400 hover:bg-red-900/20 transition-all">
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 h-14">
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white">{sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
        <span className="font-outfit font-bold text-sm text-white">Admin Panel</span>
        <button onClick={() => { logout(); navigate("/admin/login"); }} className="text-red-400"><LogOut className="w-5 h-5" /></button>
      </div>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <aside className="w-64 bg-slate-900 h-full p-4 pt-16 space-y-1" onClick={(e) => e.stopPropagation()}>
            {links.map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setSidebarOpen(false)} className={`flex items-center gap-3 px-4 py-3 rounded-xl font-outfit text-sm font-medium transition-all ${location.pathname === link.to ? "bg-orange-500 text-white" : "text-slate-400 hover:bg-slate-800"}`}>
                <link.icon className="w-5 h-5" />{link.label}
              </Link>
            ))}
          </aside>
        </div>
      )}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-4 md:p-8">{children}</div>
      </main>
    </div>
  );
}

// ─── Mini Calendar ───
function MiniCalendar({ year, month, rules, onRangeSelect, selectedStart, selectedEnd, defaultPrice }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const toISO = (d) => {
    const mm = String(month + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  };

  const getRuleForDay = (d) => {
    const iso = toISO(d);
    return rules.find((r) => r.start_date <= iso && iso <= r.end_date);
  };

  const isInSelection = (d) => {
    if (!selectedStart) return false;
    const iso = toISO(d);
    if (!selectedEnd) return iso === selectedStart;
    const [a, b] = selectedStart <= selectedEnd ? [selectedStart, selectedEnd] : [selectedEnd, selectedStart];
    return iso >= a && iso <= b;
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  return (
    <div className="select-none">
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="text-center font-outfit text-[10px] font-medium text-slate-400 py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {blanks.map((_, i) => <div key={`b${i}`} />)}
        {days.map((d) => {
          const rule = getRuleForDay(d);
          const inSel = isInSelection(d);
          const iso = toISO(d);
          const isToday = iso === new Date().toISOString().slice(0, 10);

          return (
            <button
              key={d}
              onClick={() => onRangeSelect(iso)}
              title={rule ? `€${rule.price_per_day}/day — ${rule.label || "Custom price"}` : `€${defaultPrice}/day (default)`}
              className={`
                relative h-8 w-full rounded text-xs font-outfit font-medium transition-all
                ${inSel ? "bg-orange-500 text-white" : rule ? "bg-blue-100 text-blue-800 hover:bg-blue-200" : "text-slate-700 hover:bg-slate-100"}
                ${isToday && !inSel && !rule ? "ring-1 ring-orange-400" : ""}
              `}
            >
              {d}
              {rule && !inSel && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ───
export function AdminPricing() {
  const { authHeaders } = useAuth();
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [rules, setRules] = useState([]);
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());

  // Range selection state
  const [selStart, setSelStart] = useState(null);
  const [selEnd, setSelEnd] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ price_per_day: "", label: "" });
  const [saving, setSaving] = useState(false);

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  useEffect(() => {
    axios.get(`${API}/cars?show_all=true`).then((res) => setCars(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedCar) loadRules();
  }, [selectedCar]);

  const loadRules = async () => {
    try {
      const res = await axios.get(`${API}/pricing/${selectedCar.id}`);
      setRules(res.data);
    } catch { setRules([]); }
  };

  const handleRangeSelect = (iso) => {
    if (!selStart || (selStart && selEnd)) {
      // Start new selection
      setSelStart(iso);
      setSelEnd(null);
      setShowForm(false);
    } else {
      // Complete the range
      const [a, b] = iso >= selStart ? [selStart, iso] : [iso, selStart];
      setSelStart(a);
      setSelEnd(b);
      setShowForm(true);
      setEditingRule(null);
      setForm({ price_per_day: selectedCar?.price_per_day || "", label: "" });
    }
  };

  const handleSave = async () => {
    if (!form.price_per_day || !selStart || !selEnd) {
      toast.error("Please select a date range and enter a price");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        car_id: selectedCar.id,
        start_date: selStart,
        end_date: selEnd,
        price_per_day: parseFloat(form.price_per_day),
        label: form.label || "",
      };
      if (editingRule) {
        await axios.put(`${API}/pricing/rule/${editingRule}`, payload, { headers: authHeaders });
        toast.success("Price rule updated");
      } else {
        await axios.post(`${API}/pricing/${selectedCar.id}`, payload, { headers: authHeaders });
        toast.success("Price rule added");
      }
      setSelStart(null);
      setSelEnd(null);
      setShowForm(false);
      setEditingRule(null);
      loadRules();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleEdit = (rule) => {
    setSelStart(rule.start_date);
    setSelEnd(rule.end_date);
    setForm({ price_per_day: rule.price_per_day, label: rule.label || "" });
    setEditingRule(rule.id);
    setShowForm(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm("Delete this price rule?")) return;
    try {
      await axios.delete(`${API}/pricing/rule/${ruleId}`, { headers: authHeaders });
      toast.success("Rule deleted");
      loadRules();
      if (editingRule === ruleId) {
        setShowForm(false);
        setEditingRule(null);
        setSelStart(null);
        setSelEnd(null);
      }
    } catch { toast.error("Delete failed"); }
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
    else setCalMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
    else setCalMonth(m => m + 1);
  };

  // Next month for second calendar
  const nextMonthVal = calMonth === 11 ? 0 : calMonth + 1;
  const nextMonthYear = calMonth === 11 ? calYear + 1 : calYear;

  const formatDateDisplay = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  return (
    <AdminLayout>
      <div>
        <h1 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900 mb-2">Seasonal Pricing</h1>
        <p className="font-dm-sans text-sm text-slate-400 mb-6">Set custom daily rates for specific date ranges per car.</p>

        {/* Car selector */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mb-6">
          <label className="block font-outfit text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">Select Car</label>
          <div className="flex flex-wrap gap-2">
            {cars.map((car) => (
              <button
                key={car.id}
                onClick={() => { setSelectedCar(car); setSelStart(null); setSelEnd(null); setShowForm(false); }}
                className={`px-4 py-2 rounded-lg font-outfit text-sm font-medium transition-all border ${
                  selectedCar?.id === car.id
                    ? "bg-orange-500 text-white border-orange-500 shadow-md shadow-orange-500/20"
                    : "bg-slate-50 text-slate-700 border-slate-200 hover:border-orange-300 hover:bg-orange-50"
                }`}
              >
                {car.brand} {car.model}
                <span className="ml-2 opacity-60 text-xs">€{car.price_per_day}/day</span>
              </button>
            ))}
          </div>
        </div>

        {selectedCar && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

            {/* ── Calendars ── */}
            <div className="xl:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">

                {/* Calendar nav */}
                <div className="flex items-center justify-between mb-5">
                  <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                    <ChevronLeft className="w-4 h-4 text-slate-600" />
                  </button>
                  <div className="flex gap-8">
                    <span className="font-outfit font-semibold text-slate-900">{monthNames[calMonth]} {calYear}</span>
                    <span className="font-outfit font-semibold text-slate-900">{monthNames[nextMonthVal]} {nextMonthYear}</span>
                  </div>
                  <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  </button>
                </div>

                {/* Two months side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <MiniCalendar
                    year={calYear}
                    month={calMonth}
                    rules={rules}
                    onRangeSelect={handleRangeSelect}
                    selectedStart={selStart}
                    selectedEnd={selEnd}
                    defaultPrice={selectedCar.price_per_day}
                  />
                  <MiniCalendar
                    year={nextMonthYear}
                    month={nextMonthVal}
                    rules={rules}
                    onRangeSelect={handleRangeSelect}
                    selectedStart={selStart}
                    selectedEnd={selEnd}
                    defaultPrice={selectedCar.price_per_day}
                  />
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-orange-500" />
                    <span className="font-dm-sans text-xs text-slate-500">Selected range</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300" />
                    <span className="font-dm-sans text-xs text-slate-500">Custom price set</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-slate-100 border border-slate-200" />
                    <span className="font-dm-sans text-xs text-slate-500">Default (€{selectedCar.price_per_day}/day)</span>
                  </div>
                </div>

                {/* Instructions */}
                <p className="font-dm-sans text-xs text-slate-400 mt-3">
                  Click a start date, then click an end date to select a range. Blue days already have custom prices — hover to see details.
                </p>
              </div>

              {/* Set price form — appears after range selected */}
              {showForm && selStart && selEnd && (
                <div className="bg-white rounded-xl border border-orange-200 shadow-sm p-5">
                  <h3 className="font-outfit font-semibold text-base text-slate-900 mb-4">
                    {editingRule ? "Edit Price Rule" : "Set Price for Range"}
                  </h3>
                  <div className="flex items-center gap-3 mb-4 bg-orange-50 rounded-lg px-4 py-2.5">
                    <CalendarDays className="w-4 h-4 text-orange-500" />
                    <span className="font-outfit font-medium text-sm text-slate-700">
                      {formatDateDisplay(selStart)} → {formatDateDisplay(selEnd)}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">
                        Price per day (€) *
                      </label>
                      <input
                        type="number"
                        className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-orange-400 outline-none"
                        placeholder={`e.g. ${selectedCar.price_per_day}`}
                        value={form.price_per_day}
                        onChange={(e) => setForm({ ...form, price_per_day: e.target.value })}
                      />
                      <p className="font-dm-sans text-xs text-slate-400 mt-1">Default: €{selectedCar.price_per_day}/day</p>
                    </div>
                    <div>
                      <label className="block font-outfit text-[11px] font-medium text-slate-400 mb-1 uppercase tracking-wider">
                        Label (optional)
                      </label>
                      <input
                        type="text"
                        className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:border-orange-400 outline-none"
                        placeholder="e.g. High Season, Christmas"
                        value={form.label}
                        onChange={(e) => setForm({ ...form, label: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="h-10 px-6 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-outfit text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {saving ? "Saving..." : editingRule ? "Update Rule" : "Save Rule"}
                    </button>
                    <button
                      onClick={() => { setShowForm(false); setSelStart(null); setSelEnd(null); setEditingRule(null); }}
                      className="h-10 px-4 border border-slate-200 rounded-lg font-outfit text-sm text-slate-500 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Rules list ── */}
            <div className="xl:col-span-1">
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-outfit font-semibold text-base text-slate-900">Price Rules</h3>
                  <span className="font-dm-sans text-xs text-slate-400">{rules.length} rule{rules.length !== 1 ? "s" : ""}</span>
                </div>

                {rules.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarDays className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                    <p className="font-dm-sans text-sm text-slate-400">No custom prices set.</p>
                    <p className="font-dm-sans text-xs text-slate-300 mt-1">Select a range on the calendar to add one.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {rules.map((rule) => (
                      <div
                        key={rule.id}
                        className="border border-slate-100 rounded-lg p-3 hover:border-orange-200 hover:bg-orange-50/30 transition-all group"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {rule.label && (
                              <p className="font-outfit font-medium text-xs text-orange-600 mb-0.5 truncate">{rule.label}</p>
                            )}
                            <p className="font-dm-sans text-xs text-slate-500">
                              {formatDateDisplay(rule.start_date)} → {formatDateDisplay(rule.end_date)}
                            </p>
                            <p className="font-outfit font-bold text-base text-slate-900 mt-0.5">
                              €{rule.price_per_day}<span className="font-normal text-xs text-slate-400">/day</span>
                            </p>
                            {rule.price_per_day !== selectedCar.price_per_day && (
                              <p className={`font-dm-sans text-[10px] mt-0.5 ${rule.price_per_day > selectedCar.price_per_day ? "text-emerald-600" : "text-blue-600"}`}>
                                {rule.price_per_day > selectedCar.price_per_day ? "+" : ""}
                                {Math.round(((rule.price_per_day - selectedCar.price_per_day) / selectedCar.price_per_day) * 100)}% vs default
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <button
                              onClick={() => handleEdit(rule)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-blue-100 text-slate-400 hover:text-blue-600 transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(rule.id)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-100 text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {!selectedCar && (
          <div className="text-center py-20 text-slate-300">
            <CalendarDays className="w-12 h-12 mx-auto mb-3" />
            <p className="font-outfit text-lg">Select a car above to manage its pricing</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
